// store/marketSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  MarketRegistry,
  Tab,
  MarketUI,
  MarketInputs,
  Market,
  SymbolsIndex,
  MarketCatalog
} from './types';

const TMN = 'TMN';

const toId = (base: string, quote: string) => `${base}${quote}`;

const idOf = (reg: MarketRegistry, base: string, quote: string) => {
  const id = `${base}${quote}`;
  return reg.byId[id] ? id : undefined;
};

/** Try both orientations for unordered pair {a,b} and return a valid market id */
const resolvePairId = (
  reg: MarketRegistry,
  a?: string,
  b?: string
): string | undefined => {
  if (!a || !b) return undefined;
  return idOf(reg, a, b) || idOf(reg, b, a);
};

function firstEnabledBaseForBuy(registry: MarketRegistry): string | undefined {
  const ids = registry.idsByQuote[TMN] ?? [];
  for (const id of ids) {
    const m = registry.byId[id];
    if (m?.buy?.enabled) return m.base;
  }
  return undefined;
}

function firstEnabledBaseForSell(registry: MarketRegistry): string | undefined {
  const ids = registry.idsByQuote[TMN] ?? [];
  for (const id of ids) {
    const m = registry.byId[id];
    if (m?.sell?.enabled) return m.base;
  }
  return undefined;
}

function firstOnlineQuoteForBase(
  registry: MarketRegistry,
  base: string
): string | undefined {
  const ids = registry.idsByBase[base] ?? [];
  for (const id of ids) {
    const m = registry.byId[id];
    // eslint-disable-next-line no-continue
    if (!m) continue;
    // eslint-disable-next-line no-continue
    if (m.quote === TMN) continue;
    if (m.status === 'online') return m.quote;
  }
  return undefined;
}

/** Best default partner for a symbol (either as base or quote), prefer non-TMN; online preferred but not required */
function bestPartnerForSym(
  reg: MarketRegistry,
  sym: string
): { id: string; other: string } | undefined {
  // sym as BASE → quotes
  const baseIds = reg.idsByBase[sym] ?? [];
  // online, non-TMN first
  for (const id of baseIds) {
    const m = reg.byId[id];
    if (m && m.status === 'online' && m.quote !== TMN) {
      return { id, other: m.quote };
    }
  }
  // then allow offline but non-TMN
  for (const id of baseIds) {
    const m = reg.byId[id];
    if (m && m.quote !== TMN) {
      return { id, other: m.quote };
    }
  }

  // sym as QUOTE → bases
  const quoteIds = reg.idsByQuote[sym] ?? [];
  for (const id of quoteIds) {
    const m = reg.byId[id];
    if (m && m.status === 'online' && m.base !== TMN) {
      return { id, other: m.base };
    }
  }
  for (const id of quoteIds) {
    const m = reg.byId[id];
    if (m && m.base !== TMN) {
      return { id, other: m.base };
    }
  }
  return undefined;
}

// Slice state
interface MarketSliceState {
  registry: MarketRegistry;
  symbols: SymbolsIndex;
  ui: MarketUI;
  /** Market id from a deep link, applied once the registry is hydrated. */
  pendingMarketId?: string;
  inputs: MarketInputs;
  isShowChart: boolean;
  buyStatus: boolean;
  sellStatus: boolean;
  websocketConnected: boolean;
}

const initialState: MarketSliceState = {
  registry: { byId: {}, ids: [], idsByBase: {}, idsByQuote: {} },
  symbols: {},
  buyStatus: true,
  sellStatus: true,
  isShowChart: false,
  websocketConnected: true,
  ui: {
    tab: 'buy',
    selectedMarketId: undefined,
    paySymbol: undefined,
    receiveSymbol: undefined
  },
  inputs: {
    lastChanged: 'receive',
    payTyped: '',
    receiveTyped: '',
    seedVersion: 0
  }
};

/** ---------- CENTRAL: apply default input values for current tab + market ----------
 * BUY   → fixed receive (base units): use m.buy.defaultBaseValue || '1'
 * SELL  → fixed pay (base units):     use m.sell.defaultBaseValue || '1'
 * EXCH. → fixed pay (base units):     use m.sell.defaultBaseValue || '1'
 * Clears the opposite side & sets lastChanged for the fixed side.
 * Price-based derivation is left to the UI price effect.
 */
function applyInputDefaults(state: MarketSliceState, m?: Market) {
  if (!m) return;

  if (state.ui.tab === 'buy') {
    const receive = m.buy?.defaultBaseValue || '1';
    state.inputs.receiveTyped = receive;
    state.inputs.payTyped = '';
    state.inputs.lastChanged = 'receive';
  } else {
    const pay = m.sell?.defaultBaseValue || '1';
    state.inputs.payTyped = pay;
    state.inputs.receiveTyped = '';
    state.inputs.lastChanged = 'pay';
  }

  // 🔸 trigger a one-shot recalculation in the UI
  // state.inputs.seedVersion = state.inputs.seedVersion + 1 || 0;
}

const __DEV__ = import.meta.env.MODE !== 'production';

const marketSlice = createSlice({
  name: 'otcMarket',
  initialState,
  reducers: {
    setBuyStatus: (state, action: PayloadAction<boolean>) => {
      state.buyStatus = action.payload;
    },
    setSellStatus: (state, action: PayloadAction<boolean>) => {
      state.sellStatus = action.payload;
    },
    hydrateRegistry(state, a: PayloadAction<MarketCatalog>) {
      state.registry = a.payload.registry;
      state.symbols = a.payload.symbols;
      const reg = state.registry;
      const { tab } = state.ui;

      // Re-hydrating (a remount, or the selectors changing mid-session) must not
      // throw away the market the user is on: keep it while it is still valid.
      const current = state.ui.selectedMarketId
        ? reg.byId[state.ui.selectedMarketId]
        : undefined;
      const currentStillValid =
        !!current &&
        current.quote === TMN &&
        !!state.ui.paySymbol &&
        !!state.ui.receiveSymbol &&
        (tab === 'buy'
          ? current.buy?.enabled
          : tab === 'sell'
          ? current.sell?.enabled
          : false);

      if (currentStillValid) {
        // nothing to reset
      } else if (tab === 'buy') {
        const base = firstEnabledBaseForBuy(reg);
        if (base) {
          const id = toId(base, TMN);
          state.ui.selectedMarketId = id;
          state.ui.paySymbol = TMN;
          state.ui.receiveSymbol = base;
          applyInputDefaults(state, reg.byId[id]);
        }
      } else if (tab === 'sell') {
        const base = firstEnabledBaseForSell(reg);
        if (base) {
          const id = toId(base, TMN);
          state.ui.selectedMarketId = id;
          state.ui.paySymbol = base;
          state.ui.receiveSymbol = TMN;
          applyInputDefaults(state, reg.byId[id]);
        }
      } else {
        // exchange: if no valid non-TMN market selected, pick any online non-TMN and default to quote→base
        const keepId = state.ui.selectedMarketId;
        const keep = keepId ? reg.byId[keepId] : undefined;

        if (!keep || keep.quote === TMN || keep.status !== 'online') {
          for (const id of reg.ids) {
            const m = reg.byId[id];
            if (m && m.status === 'online' && m.quote !== TMN) {
              state.ui.selectedMarketId = id;
              state.ui.paySymbol = m.quote; // default orientation; user can flip later
              state.ui.receiveSymbol = m.base;
              applyInputDefaults(state, m);
              break;
            }
          }
        } else {
          // we have a valid non-TMN market; seed defaults with it
          applyInputDefaults(state, keep);
        }
      }

      // A deep link (?market=) wins over the tab defaults picked above.
      const pending = state.pendingMarketId;
      state.pendingMarketId = undefined;
      if (pending && reg.byId[pending]) {
        marketSlice.caseReducers.setSelectedMarketId(state, {
          type: 'otcMarket/setSelectedMarketId',
          payload: pending
        });
      }
    },

    setPendingMarketId(state, a: PayloadAction<string | undefined>) {
      state.pendingMarketId = a.payload;
      // Registry already there (revisit): apply now; hydrateRegistry re-applies
      // it if it runs afterwards and resets the defaults.
      if (a.payload && state.registry.byId[a.payload]) {
        marketSlice.caseReducers.setSelectedMarketId(state, {
          type: 'otcMarket/setSelectedMarketId',
          payload: a.payload
        });
      }
    },

    setTab(state, a: PayloadAction<{ tab: Tab; preserveMarket?: boolean }>) {
      const { tab: next, preserveMarket = false } = a.payload;

      if (state.ui.tab === next) return;

      const previous = state.ui.selectedMarketId
        ? state.registry.byId[state.ui.selectedMarketId]
        : undefined;

      state.ui.tab = next;
      const reg = state.registry;

      // preserveMarket keeps the market but the pay/receive orientation belongs
      // to the tab, so re-derive it. With no market to keep, use the defaults.
      if (preserveMarket && previous) {
        marketSlice.caseReducers.setSelectedMarketId(state, {
          type: 'otcMarket/setSelectedMarketId',
          payload: previous.id
        });
        return;
      }

      /*
       * The crypto the reader had chosen follows them across the tabs.
       *
       * It is the market's base in all three: what you buy in خرید, what you
       * sell in فروش, and what you get in تبدیل, where the quote is the stable
       * side the pair is priced in. Each tab still needs its own pay/receive
       * orientation and market id, so the asset is carried and the rest is
       * re-derived below — a tab falls back to its own default only when the
       * carried asset has no market to trade on there.
       */
      const carried =
        previous && previous.base !== TMN ? previous.base : undefined;

      // Existing logic for resetting to defaults
      if (next === 'buy') {
        const carriedIsBuyable =
          carried && reg.byId[toId(carried, TMN)]?.buy?.enabled;
        const base = carriedIsBuyable ? carried : firstEnabledBaseForBuy(reg);
        if (base && reg.byId[toId(base, TMN)]) {
          const id = toId(base, TMN);
          state.ui.selectedMarketId = id;
          state.ui.paySymbol = TMN;
          state.ui.receiveSymbol = base;
          applyInputDefaults(state, reg.byId[id]);
        } else {
          state.inputs.lastChanged = 'receive';
          state.inputs.payTyped = '';
          state.inputs.receiveTyped = '';
        }
        return;
      }

      if (next === 'sell') {
        const carriedIsSellable =
          carried && reg.byId[toId(carried, TMN)]?.sell?.enabled;
        const base = carriedIsSellable ? carried : firstEnabledBaseForSell(reg);
        if (base && reg.byId[toId(base, TMN)]) {
          const id = toId(base, TMN);
          state.ui.selectedMarketId = id;
          state.ui.paySymbol = base;
          state.ui.receiveSymbol = TMN;
          applyInputDefaults(state, reg.byId[id]);
        } else {
          state.inputs.lastChanged = 'pay';
          state.inputs.payTyped = '';
          state.inputs.receiveTyped = '';
        }
        return;
      }

      /*
       * exchange: the carried asset keeps the side it was already on — the one
       * you were selling stays the one you pay with, the one you were buying
       * stays the one you receive — and its partner is the market it trades
       * against. `paySymbol` is still the previous tab's, since only `tab` has
       * been written so far.
       */
      const carriedWasPay = !!carried && state.ui.paySymbol === carried;
      const partner = carried ? bestPartnerForSym(reg, carried) : undefined;
      if (carried && partner) {
        state.ui.selectedMarketId = partner.id;
        state.ui.paySymbol = carriedWasPay ? carried : partner.other;
        state.ui.receiveSymbol = carriedWasPay ? partner.other : carried;
        applyInputDefaults(state, reg.byId[partner.id]);
        return;
      }

      // exchange logic (unchanged)
      for (const id of reg.ids) {
        const m = reg.byId[id];
        if (m && m.status === 'online' && m.quote !== TMN) {
          state.ui.selectedMarketId = id;
          state.ui.paySymbol = m.quote;
          state.ui.receiveSymbol = m.base;
          applyInputDefaults(state, m);
          break;
        }
      }
    },

    setSelectedMarketId(state, a: PayloadAction<string | undefined>) {
      const id = a.payload;
      if (!id) return;
      const m = state.registry.byId[id];
      if (!m) return;

      state.ui.selectedMarketId = id;

      if (state.ui.tab === 'buy') {
        state.ui.paySymbol = TMN;
        state.ui.receiveSymbol = m.base;
        // ensure we land on BASE/TMN id
        const tmnId = m.quote === TMN ? id : toId(m.base, TMN);
        const mm = state.registry.byId[tmnId] || m;
        state.ui.selectedMarketId = mm.id;
        applyInputDefaults(state, mm);
        return;
      }

      if (state.ui.tab === 'sell') {
        state.ui.paySymbol = m.base;
        state.ui.receiveSymbol = TMN;
        const tmnId = m.quote === TMN ? id : toId(m.base, TMN);
        const mm = state.registry.byId[tmnId] || m;
        state.ui.selectedMarketId = mm.id;
        applyInputDefaults(state, mm);
        return;
      }

      // === exchange === (keep exact id; normalize symbols if missing/inconsistent)
      const pay = state.ui.paySymbol;
      const recv = state.ui.receiveSymbol;

      const payIsIn = pay === m.base || pay === m.quote;
      const recvIsIn = recv === m.base || recv === m.quote;

      if (payIsIn && recvIsIn && pay !== recv) {
        // ok
      } else if (payIsIn) {
        state.ui.receiveSymbol = pay === m.quote ? m.base : m.quote;
      } else if (recvIsIn) {
        state.ui.paySymbol = recv === m.base ? m.quote : m.base;
      } else {
        // default (quote→base)
        state.ui.paySymbol = m.quote;
        state.ui.receiveSymbol = m.base;
      }
      applyInputDefaults(state, m);
    },

    // === EXCHANGE symbol picking (unordered pair search) ===
    setPaySymbol(state, a: PayloadAction<string | undefined>) {
      const sym = a.payload;
      if (!sym || state.ui.tab !== 'exchange') return;

      const reg = state.registry;
      const curRecv = state.ui.receiveSymbol;

      // Keep current receive if pair exists (any orientation)
      const keepId = resolvePairId(reg, sym, curRecv);
      if (keepId) {
        const m = reg.byId[keepId]!;
        state.ui.selectedMarketId = keepId;
        state.ui.paySymbol = sym;
        state.ui.receiveSymbol = sym === m.quote ? m.base : m.quote;
        applyInputDefaults(state, m);
      } else {
        // No pair with current receive → pick a default tradable partner for sym
        const pick = bestPartnerForSym(reg, sym);
        if (!pick) return;
        const { id } = pick;
        const m = reg.byId[id]!;
        state.ui.selectedMarketId = id;
        state.ui.paySymbol = sym;
        state.ui.receiveSymbol = sym === m.quote ? m.base : m.quote;
        applyInputDefaults(state, m);
      }
    },

    setReceiveSymbol(state, a: PayloadAction<string | undefined>) {
      const sym = a.payload;
      if (!sym) return;

      if (state.ui.tab === 'exchange') {
        const reg = state.registry;
        const pay = state.ui.paySymbol;
        const id = resolvePairId(reg, pay, sym);
        if (!id) return;
        const m = reg.byId[id]!;
        state.ui.selectedMarketId = id;
        state.ui.receiveSymbol = sym;
        state.ui.paySymbol = sym === m.base ? m.quote : m.base;
        applyInputDefaults(state, m);
        return;
      }

      // BUY/SELL unchanged
      if (state.ui.tab === 'sell') return;
      if (state.ui.tab === 'buy') {
        const id = idOf(state.registry, sym, TMN);
        if (!id) return;
        const m = state.registry.byId[id]!;
        state.ui.selectedMarketId = id;
        state.ui.paySymbol = TMN;
        state.ui.receiveSymbol = sym;
        applyInputDefaults(state, m);
      }
    },

    // --- user inputs ---
    setPayTyped(state, a: PayloadAction<string>) {
      state.inputs.payTyped = a.payload;
      state.inputs.lastChanged = 'pay';
    },
    setReceiveTyped(state, a: PayloadAction<string>) {
      state.inputs.receiveTyped = a.payload;
      state.inputs.lastChanged = 'receive';
    },

    setPayDerived(state, a: PayloadAction<string>) {
      state.inputs.payTyped = a.payload;
    },
    setReceiveDerived(state, a: PayloadAction<string>) {
      state.inputs.receiveTyped = a.payload;
    },

    applyPayChange(
      state,
      a: PayloadAction<{ clean: string; receiveAmount: string }>
    ) {
      state.inputs.payTyped = a.payload.clean;
      state.inputs.receiveTyped = a.payload.receiveAmount;
      state.inputs.lastChanged = 'pay';
    },
    applyReceiveChange(
      state,
      a: PayloadAction<{ clean: string; payAmount: string }>
    ) {
      state.inputs.receiveTyped = a.payload.clean;
      state.inputs.payTyped = a.payload.payAmount;
      state.inputs.lastChanged = 'receive';
    },

    clearInputs(state) {
      state.inputs.payTyped = '';
      state.inputs.receiveTyped = '';
    },

    swap(state) {
      const id = state.ui.selectedMarketId;
      const m = id ? state.registry.byId[id] : undefined;
      if (!m) return;

      if (state.ui.tab === 'exchange') {
        // keep market, just flip symbols (direction)
        const p = state.ui.paySymbol;
        state.ui.paySymbol = state.ui.receiveSymbol;
        state.ui.receiveSymbol = p;
        // re-seed defaults for the new direction
        applyInputDefaults(state, m);
        return;
      }

      // BUY/SELL toggling keeps the same BASE/TMN market
      if (state.ui.tab === 'buy') {
        state.ui.tab = 'sell';
        state.ui.paySymbol = m.base;
        state.ui.receiveSymbol = m.quote; // TMN
        applyInputDefaults(state, m);
      } else {
        state.ui.tab = 'buy';
        state.ui.paySymbol = m.quote; // TMN
        state.ui.receiveSymbol = m.base;
        applyInputDefaults(state, m);
      }
    },
    toggleIsShowChart: (state) => {
      state.isShowChart = !state.isShowChart;
    },
    closeChart: (state) => {
      state.isShowChart = false;
    },
    setWebSocketStatus(state, action: PayloadAction<boolean>) {
      state.websocketConnected = action.payload;
    }
  }
});

export const {
  hydrateRegistry,
  setTab,
  setSelectedMarketId,
  setPendingMarketId,
  setPaySymbol,
  setReceiveSymbol,
  setPayTyped,
  setReceiveTyped,
  clearInputs,
  swap,
  setPayDerived,
  setReceiveDerived,
  applyReceiveChange,
  applyPayChange,
  setSellStatus,
  setBuyStatus,
  toggleIsShowChart,
  closeChart,
  setWebSocketStatus
} = marketSlice.actions;

export default marketSlice.reducer;
